import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import { Box, Collapse, Popover, Stack, Typography, Avatar } from "@mui/material";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useDispatch } from "react-redux";
import { hubAction } from "redux/slice/hub";
import remarkEmoji from "remark-emoji";
import rehypeRaw from "rehype-raw";
import TooltipDark from "utils/TooltipDark";
import { toHTML } from "slack-markdown";
import { homeAction } from "redux/slice/home";
import EmojiBox from "./EmojiBox";
import EmojiPopover from "./EmojiPopover";
import WinWireReplyInput from "./WinWireReplyInput";
import WinWireReaction from "./WinWireReaction";
import WinWireReply from "./WinWireReply";

const WinWireMessage = forwardRef(({ message, user, companyLogo, highlightedMessage }, ref) => {
  const iconPath = "/logos/alysio_icon_white.svg";
  const replySectionRef = useRef();
  const dispatch = useDispatch();
  const [isHovered, setIsHovered] = useState(false);
  const [isReplyHovered, setIsReplyHovered] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState(null);
  // const isMobileDisplay = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  useEffect(() => {
    if (highlightedMessage === message.id && !showReplies) {
      setShowReplies(true);
    }
  }, [highlightedMessage]);

  const parsedMessage = useMemo(
    () => (
      <ReactMarkdown remarkPlugins={[remarkEmoji]} rehypePlugins={[rehypeRaw]}>
        {message?.content
          ? toHTML(message.content).replace(/ {2,}/g, (match) => "&nbsp;".repeat(match.length))
          : ""}
      </ReactMarkdown>
    ),
    [message]
  );

  const sortReactions = (reactions) => {
    const counts = reactions.reduce((acc, reaction) => {
      if (!acc[reaction.reaction]) {
        acc[reaction.reaction] = 0;
      }
      acc[reaction.reaction] += 1;
      return acc;
    }, {});
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  };

  const sortedReactions = sortReactions(message.WinWireReactions);

  const handleReplyClick = () => {
    dispatch(homeAction.clearNotificationsForWinWireMessage({ messageId: message.id }));
    setShowReplies((prev) => !prev);
  };

  const handleSendReply = (content, taggedUsers) => {
    if (content.trim() !== "") {
      dispatch(
        hubAction.sendWinWireReplySaga({
          messageId: message.id,
          content,
          taggedUsers,
        })
      );
    }
  };

  const handleEditReply = (replyId, content, taggedUsers) => {
    if (content.trim() !== "") {
      dispatch(
        hubAction.updateWinWireReplySaga({
          messageId: message.id,
          replyId,
          content,
          taggedUsers,
        })
      );
    }
  };

  const handleDeleteReply = (replyId) => {
    dispatch(
      hubAction.deleteWinWireReplySaga({
        messageId: message.id,
        replyId,
      })
    );
  };

  // Quick reaction box
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // Emoji popover
  const popoverOpen = Boolean(anchorPosition);
  const id = popoverOpen ? `emoji-popover-${message.id}` : undefined;
  const [popoverReplySource, setPopoverReplySource] = useState(null);

  const handleOpenEmojiPopover = (event, reply = null) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setPopoverReplySource(reply);
  };

  const handleCloseEmojiPopover = () => {
    setAnchorPosition(null);
    setPopoverReplySource(null);
  };

  const handleDispatchReaction = (reaction, reply = null) => {
    const target = reply || message;
    const doesUserHaveReaction = target.WinWireReactions.some(
      (r) => r.reaction === reaction && r.FK_user_companies_links_id === user.userCompanyLinkId
    );
    const payload = {
      messageId: message.id,
      replyId: reply ? reply.id : null,
      reaction,
    };
    if (doesUserHaveReaction) {
      // Remove reaction
      dispatch(hubAction.removeReaction(payload));
    } else {
      // Add reaction
      dispatch(hubAction.addReaction(payload));
    }
  };

  const handleReactionClick = (reaction) => {
    handleDispatchReaction(reaction, popoverReplySource);
  };

  const renderReaction = (reaction, count) => {
    const doesUserHaveReaction = message.WinWireReactions.some(
      (r) => r.reaction === reaction && r.FK_user_companies_links_id === user.userCompanyLinkId
    );
    return (
      <WinWireReaction
        key={reaction}
        allReactions={message.WinWireReactions}
        reaction={reaction}
        count={count}
        userReaction={doesUserHaveReaction}
        handleReactionClick={handleReactionClick}
      />
    );
  };

  const calculateTimeAgo = () => {
    let timeAgo = "";
    if (message.WinWireReplies.length > 0) {
      const latestMessage = message.WinWireReplies[message.WinWireReplies.length - 1];
      timeAgo = moment.tz(latestMessage.createdAt, user.timezone).fromNow();
    }
    return timeAgo;
  };

  const renderIcon = () => {
    if (companyLogo) {
      return (
        <Avatar className="company-logo" variant="circular" alt="Company logo" src={companyLogo} />
      );
    }
    return (
      <Box className="icon-box">
        <Box component="img" className="icon" src={iconPath} alt="Alysio logo" />
      </Box>
    );
  };

  const checkVisibilityWithObserver = (inRef, options = {}) =>
    new Promise((resolve) => {
      if (!inRef.current) {
        resolve(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          resolve(entry.isIntersecting);
          observer.disconnect();
        },
        {
          threshold: options.threshold || 0,
          rootMargin: options.rootMargin || "0px",
          root: options.root || null,
        }
      );

      observer.observe(inRef.current);
    });

  const executeScroll = async () => {
    if (!(await checkVisibilityWithObserver(replySectionRef, { threshold: 0.4 })))
      replySectionRef?.current?.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
      });
  };

  return (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`win-wire-message-wrapper ${
        highlightedMessage === message.id ? "highlighted" : ""
      }`}
    >
      {isHovered && !isReplyHovered && !popoverOpen && (
        <EmojiBox
          handleReactionClick={handleReactionClick}
          handleOpenEmojiPopover={handleOpenEmojiPopover}
        />
      )}
      <Box className="win-wire-message">
        <Stack direction="row" sx={{ width: "100%" }}>
          {renderIcon()}
          <Stack flexGrow={1}>
            <Stack direction="row" alignItems="center">
              <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>Win-Wire</Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.85rem", color: "#888", ml: "0.5rem" }}
              >
                {moment.tz(message.createdAt, user.timezone).format("M/D h:mma")}
              </Typography>
            </Stack>
            <Box className="markdown">{parsedMessage}</Box>
            {message.WinWireReactions && message.WinWireReactions.length ? (
              <Stack direction="row" alignItems="center" flexWrap="wrap">
                {sortedReactions.map(([reaction, count]) => renderReaction(reaction, count))}
                <TooltipDark title="Add reaction" placement="top">
                  <Box className="reaction-emoji" onClick={handleOpenEmojiPopover}>
                    <AddReactionOutlinedIcon className="add-reaction-emoji" />
                  </Box>
                </TooltipDark>
              </Stack>
            ) : null}
            {message.WinWireReplies?.length ? (
              <Box className="reply-headline">
                <ChatBubbleRoundedIcon sx={{ mr: "0.25rem" }} />
                <Typography className="reply-headline-link" onClick={handleReplyClick}>
                  {`${message.WinWireReplies.length} comment${
                    message.WinWireReplies.length > 1 ? "s" : ""
                  }`}
                </Typography>
                <Typography sx={{ color: "#888", fontSize: "0.8rem" }}>
                  {calculateTimeAgo()}
                </Typography>
              </Box>
            ) : (
              <Box className="reply-headline">
                {isHovered && (
                  <Typography className="reply-headline-link-gray" onClick={handleReplyClick}>
                    Add reply
                  </Typography>
                )}
              </Box>
            )}
            <Collapse in={showReplies} timeout="auto" unmountOnExit onEntered={executeScroll}>
              <Box className="replies-container" ref={replySectionRef}>
                <Stack>
                  {message.WinWireReplies.map((reply) => (
                    <WinWireReply
                      key={reply.id}
                      user={user}
                      reply={reply}
                      sortReactions={sortReactions}
                      setIsReplyHovered={setIsReplyHovered}
                      handleOpenEmojiPopover={handleOpenEmojiPopover}
                      handleDispatchReaction={handleDispatchReaction}
                      handleDeleteReply={handleDeleteReply}
                      handleEditReply={handleEditReply}
                    />
                  ))}
                </Stack>
                <Box>
                  <WinWireReplyInput handleSendReply={handleSendReply} message={message} />
                </Box>
              </Box>
            </Collapse>
          </Stack>
        </Stack>
      </Box>
      <Popover
        id={id}
        open={popoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        onClose={handleCloseEmojiPopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPopover-paper": {
            p: 0,
          },
        }}
      >
        <EmojiPopover
          handleReactionClick={handleReactionClick}
          handleCloseEmojiPopover={handleCloseEmojiPopover}
        />
      </Popover>
    </Box>
  );
});

export default WinWireMessage;
